<!--
  Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">View Saved Timing</h1>
<br/>
@if (permission()) {
  <div>
    @if (loading || exporting || viewLoading() || exportingReport()) {
      <p-progressBar mode="indeterminate" [style]="{ height: '6px' }"></p-progressBar>
    }
    <table style="width:100%">
      <tr>
        <td><h3 style="font-size: x-large; font-weight: bold">{{getPrefix()}} Timing Chart</h3></td>
        <td><div class="buttons" style="float: right;">
        <p-select [options]="types" [(ngModel)]="type" [disabled]="isViewSavedTypeSelectorDisabled()" (ngModelChange)="clearProgramRunsFromGrid()"
          placeholder="Template Type" class="mr-2">
        </p-select>
        <p-button label="Select Program Timings" icon="pi pi-search" class="gm-btn-primary mr-2"
          [severity]="!programRuns.get(type) || !programRuns.get(type).length ? 'secondary' : null"
          [disabled]="!programRuns.get(type) || !programRuns.get(type).length" (click)="showTimingSelectionModal = true; onResize();">
        </p-button>
        @if (adminPermission() && !ktsTimingTemplateRowMap.get(type)) {
          <p-button label="Delete Program Timings" icon="pi pi-trash" severity="danger" class="mr-2"
            [severity]="!programRuns.get(type) || !programRuns.get(type).length ? 'secondary' : 'danger'"
            [disabled]="!programRuns.get(type) || !programRuns.get(type).length" (click)="showTimingDeletionModal = true; onResize();">
          </p-button>
        }
          @if ((ktsTimingTemplateRowMap?.size ?? 0) > 0) {
            <p-button label="Clear Program Timings"
            severity="warn" (click)="clearProgramRunsFromGrid()" class="mr-2" icon="pi pi-replay"></p-button>
            <p-splitbutton icon="pi pi-download" [disabled]="exportingReport()"
              label="Export Timings" class="mr-2"
            [model]="visibleDownloadButtonOptions"></p-splitbutton>
          }
          @if ((ktsTimingTemplateRowMap?.size ?? 0) === 0 && visibleExportOptions.length && !!programRuns.get(type)?.length) {
            <p-splitbutton icon="pi pi-download" [disabled]="exporting"
              [severity]="exporting ? 'secondary' : null"
              label="Export All Report" class="mr-2"
            [model]="visibleExportOptions"></p-splitbutton>
          }
          @if ((ktsTimingTemplateRowMap?.size ?? 0) > 0 && visibleExportOptions.length) {
            <p-splitbutton icon="pi pi-download" class="mr-2"
              label="Export Selected Report"
            [model]="visibleExportOptions"></p-splitbutton>
          }
          @if ((ktsTimingTemplateRowMap?.size ?? 0) > 0) {
            <p-splitbutton icon="pi pi-filter"
              label="{{filterDuration > 0 ? 'Within ' + filterDuration + ' Days' : '30/60/90'}}" class="mr-2"
            [model]="durationButtonOptions"></p-splitbutton>
          }
        </div></td>
      </tr>
    </table>

  @if (showSelectTimingsPrompt()) {
    <p-message severity="info" styleClass="select-timings-banner" data-testid="select-timings-banner">
      Click <strong>Select Program Timings</strong> to choose the saved timings to view.
    </p-message>
  }

  @if (ktsTimingTemplateRowMap.get(type)?.size) {
    <p-tabs
      [value]="safeActiveTabIndex" (valueChange)="setActiveTabIndex(+$event); handleTabClick()">
      <p-tablist>
        @for (entry of ktsTimingTemplateRowMap.get(type) | keyvalue; track entry; let i = $index) {
          <p-tab [value]="i">
            <span>
              <i class="pi pi-calendar"></i>
              Version {{entry.key}} ({{ktsTimingTemplateRowMap.get(type).get(entry.key)[0].programData.size}})
            </span>
          </p-tab>
        }
        @if (ktsTimingTemplateRowMap?.size) {
          <p-tab [value]="ktsTimingTemplateRowMap.get(type)?.size">
            <span>
              <i class="pi pi-calendar-minus"></i>
              Gantt Chart
            </span>
          </p-tab>
        }
      </p-tablist>
      <p-tabpanels>
        @for (entry of ktsTimingTemplateRowMap.get(type) | keyvalue; track entry; let i = $index) {
          <p-tabpanel [value]="i">
            <p>
              <ag-grid-angular style="width: 100%; height: 80vh;" class="ag-theme-balham key-task-scheduler"
                [paginationPageSize]="paginationPageSize" [rowHeight]="30"
                (columnVisible)="onColumnVisible($event, entry.key)"
                [paginationPageSizeSelector]="paginationPageSizeSelector"
                [columnDefs]="fullCols.get(type).get(entry.key)"
                [rowData]="ktsTimingTemplateRowMap.get(type).get(entry.key)" [pagination]="true"
                [autoSizeStrategy]="autoSizeStrategy" [rowClassRules]="rowClassRules"
                [defaultColDef]="defaultColDef" [enableBrowserTooltips]="true"
              (gridReady)="onGridReady($event, entry.key)"></ag-grid-angular>
            </p>
          </p-tabpanel>
        }
        @if (ktsTimingTemplateRowMap?.size) {
          <p-tabpanel [value]="ktsTimingTemplateRowMap.get(type)?.size">
            <div>
              @if (activeTabIndexSignal() === ktsTimingTemplateRowMap.get(type)?.size) {
                <app-gantt-chart app-gantt-chart
                [ganttChart]="ganttChart"></app-gantt-chart>
              }
            </div>
          </p-tabpanel>
        }
      </p-tabpanels>
    </p-tabs>
  }
    <div style="display: block; width: 1px; height: 1px; overflow: hidden;">
      <div #imageExportElement>
        @if (exportingGantt) {
          <app-gantt-chart #exportGantt app-gantt-chart [ganttChart]="ganttChart" trueSize="true"/>
        }
      </div>
    </div>
  </div>
}
@if (programRuns.get(type) && programRuns.get(type).length) {
  <p-dialog header="Select {{type}} Program Timings" [(visible)]="showTimingSelectionModal"
    [modal]="true" [closable]="true" [style]="selectModalStyle" [styleClass]="selectModalStyleClass" resizable="true">
    <div style="width: 100%; height: 70vh;">
      <ag-grid-angular class="ag-theme-balham" style="width: 100%; height: 100%;" [rowData]="programRuns.get(type)"
        [columnDefs]="selectColumnDefs" rowSelection="multiple" [rowMultiSelectWithClick]="true"
        [defaultColDef]="defaultColDef" [enableBrowserTooltips]="true" [paginationPageSize]="500"
        [pagination]="true" [paginationPageSizeSelector]="paginationPageSizeSelector"
        (gridReady)="onModalGridReady($event)" (selectionChanged)="onSelectionChanged()">
      </ag-grid-angular>
      <p>Selected: {{ selectedProgramRuns.length }}</p>
    </div>
    <div class="buttons" style="float: right;">
      <p-button gmSecondary label="Clear" class="mr-2" icon="pi pi-times"
      (onClick)="clearModalSelection(modalGridApi)"></p-button>
      <p-button label="View Program Timings" icon="pi pi-calendar-plus" class="gm-btn-primary mr-2"
        [severity]="selectedProgramRuns.length === 0 ? 'secondary' : null"
        [disabled]="selectedProgramRuns.length === 0"
        (click)="showProgramTiming(selectedProgramRuns);">
      </p-button>
      @if (visibleExportOptions.length) {
        <p-splitbutton icon="pi pi-download" class="mr-2" [disabled]="selectedProgramRuns.length === 0"
          [severity]="selectedProgramRuns.length === 0 ? 'secondary' : null"
          label="Export Selected Reports"
        [model]="visibleExportOptions"></p-splitbutton>
      }
    </div>
  </p-dialog>
}
@if (programRuns.get(type) && programRuns.get(type).length) {
  <p-dialog header="Delete Program Timings" [(visible)]="showTimingDeletionModal"
    [modal]="true" [closable]="true" [style]="deleteModalStyle" resizable="true"
    [class]="'delete-warning-modal'">
    <ng-template pTemplate="header">
      <div style="display: flex; align-items: center; color: #dc3545;">
        <i class="pi pi-exclamation-triangle" style="font-size: 1.5rem; margin-right: 0.5rem;"></i>
        <span style="font-weight: bold; font-size: 1.25rem;">Delete {{type}} Program Timings</span>
      </div>
    </ng-template>
    <div style="width: 100%; padding: 1rem; background-color: #fff3cd; border: 2px solid #ffc107; border-radius: 4px; margin-top: 1rem; margin-bottom: 1rem;">
      <div style="display: flex; align-items: center; color: #856404;">
        <i class="pi pi-exclamation-triangle" style="font-size: 1.25rem; margin-right: 0.5rem;"></i>
        <strong>Warning:</strong>
      </div>
      <p style="margin: 0.5rem 0 0 0; color: #856404; padding-left: 1.75rem;">
        You are about to delete program timings. This action cannot be undone. Please review your selections carefully before proceeding.
      </p>
    </div>
    <div style="width: 100%; height: 60vh; min-height: 400px;">
      <ag-grid-angular class="ag-theme-balham" style="width: 100%; height: 100%;" [rowData]="programRuns.get(type)"
        [columnDefs]="selectColumnDefs" rowSelection="multiple" [rowMultiSelectWithClick]="true"
        [defaultColDef]="defaultColDef" [enableBrowserTooltips]="true" [paginationPageSize]="500"
        [pagination]="true" [paginationPageSizeSelector]="paginationPageSizeSelector"
        (gridReady)="onDeleteModalGridReady($event)" (selectionChanged)="onDeleteSelectionChanged()">
      </ag-grid-angular>
      <p>Selected: {{ selectedProgramRunsToDelete.length }}</p>
    </div>
    <div class="buttons" style="float: right; margin-top: 1rem;">
      <p-button gmSecondary label="Clear" class="mr-2" icon="pi pi-times"
      (onClick)="clearModalSelection(deleteModalApi)"></p-button>
      <p-button label="Delete Program Timing" icon="pi pi-trash" severity="danger" class="mr-2"
        [severity]="selectedProgramRunsToDelete.length === 0 ? 'secondary' : 'danger'"
        [disabled]="selectedProgramRunsToDelete.length === 0"
        (click)="deleteProgramRuns(selectedProgramRunsToDelete);">
      </p-button>
      @if (visibleExportOptions.length) {
        <p-splitbutton icon="pi pi-download" class="mr-2" [disabled]="selectedProgramRunsToDelete.length === 0"
          [severity]="selectedProgramRunsToDelete.length === 0 ? 'secondary' : null"
          label="Export Selected Reports"
        [model]="visibleExportOptions"></p-splitbutton>
      }
    </div>
  </p-dialog>
}










